import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

// ✅ Fetch user's custom pricing rules
export const fetchUserCustomPricing = createAsyncThunk(
  "userCustomPricing/fetchRules",
  async (profile_id, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_URL}/users/${profile_id}/custom-pricing/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 
        error.response?.data?.detail || 
        "فشل في تحميل قواعد التسعير"
      );
    }
  }
);

// ✅ Search products for pricing
export const searchProductsForPricing = createAsyncThunk(
  "userCustomPricing/searchProducts",
  async (query, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_URL}/products/search-for-pricing/?q=${encodeURIComponent(query)}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 
        error.response?.data?.detail || 
        "فشل في البحث عن المنتجات"
      );
    }
  }
);

// ✅ Create pricing rule
export const createPricingRule = createAsyncThunk(
  "userCustomPricing/createRule",
  async ({ profile_id, ruleData }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_URL}/users/${profile_id}/custom-pricing/`,
        ruleData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 
        error.response?.data?.message || 
        "فشل في إضافة قاعدة التسعير"
      );
    }
  }
);

// ✅ Update pricing rule
export const updatePricingRule = createAsyncThunk(
  "userCustomPricing/updateRule",
  async ({ profile_id, rule_id, ruleData }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `${API_URL}/users/${profile_id}/custom-pricing/${rule_id}/`,
        ruleData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
        }
      );
      // ✅ إرجاع rule_id معاه عشان نعرف نحدث الصح
      return { ...response.data, id: rule_id };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 
        error.response?.data?.message || 
        "فشل في تحديث قاعدة التسعير"
      );
    }
  }
);

// ✅ Delete pricing rule
export const deletePricingRule = createAsyncThunk(
  "userCustomPricing/deleteRule",
  async ({ profile_id, rule_id }, { rejectWithValue }) => {
    try {
      await axios.delete(
        `${API_URL}/users/${profile_id}/custom-pricing/${rule_id}/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );
      return rule_id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 
        error.response?.data?.message || 
        "فشل في حذف قاعدة التسعير"
      );
    }
  }
);

const initialState = {
  userData: null,
  rules: [],
  searchResults: [],
  isLoading: false,
  isSearching: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  error: null,
  searchError: null,
};

const userCustomPricingSlice = createSlice({
  name: "userCustomPricing",
  initialState,
  reducers: {
    clearSearchResults: (state) => {
      state.searchResults = [];
      state.searchError = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetState: (state) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      // ✅ Fetch rules
      .addCase(fetchUserCustomPricing.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserCustomPricing.fulfilled, (state, action) => {
        state.isLoading = false;
        state.userData = action.payload.user;
        state.rules = action.payload.rules || [];
        state.error = null;
      })
      .addCase(fetchUserCustomPricing.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // ✅ Search products
      .addCase(searchProductsForPricing.pending, (state) => {
        state.isSearching = true;
        state.searchError = null;
      })
      .addCase(searchProductsForPricing.fulfilled, (state, action) => {
        state.isSearching = false;
        state.searchResults = action.payload.results || [];
        state.searchError = null;
      })
      .addCase(searchProductsForPricing.rejected, (state, action) => {
        state.isSearching = false;
        state.searchError = action.payload;
        state.searchResults = [];
      })

      // ✅ Create rule
      .addCase(createPricingRule.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createPricingRule.fulfilled, (state, action) => {
        state.isCreating = false;
        // ✅ Add the new rule with all its data
        const newRule = {
          ...action.payload,
          // ✅ Ensure dates are present (fallback to current time if missing)
          created_at: action.payload.created_at || new Date().toISOString(),
          updated_at: action.payload.updated_at || new Date().toISOString(),
        };
        state.rules.push(newRule);
        state.error = null;
      })
      .addCase(createPricingRule.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload;
      })

      // ✅ Update rule
      .addCase(updatePricingRule.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updatePricingRule.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.rules.findIndex((r) => r.id === action.payload.id);
        if (index !== -1) {
          // ✅ Preserve existing data and update with new data
          state.rules[index] = {
            ...state.rules[index],
            ...action.payload,
            product: action.payload.product || state.rules[index].product,
            // ✅ Update the updated_at timestamp
            updated_at: action.payload.updated_at || new Date().toISOString(),
          };
        }
        state.error = null;
      })
      .addCase(updatePricingRule.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload;
      })

      // ✅ Delete rule
      .addCase(deletePricingRule.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deletePricingRule.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.rules = state.rules.filter((r) => r.id !== action.payload);
        state.error = null;
      })
      .addCase(deletePricingRule.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload;
      });
  },
});

export const { clearSearchResults, clearError, resetState } = userCustomPricingSlice.actions;

// ✅ Selectors
export const selectUserData = (state) => state.userCustomPricing.userData;
export const selectPricingRules = (state) => state.userCustomPricing.rules;
export const selectSearchResults = (state) => state.userCustomPricing.searchResults;
export const selectIsLoading = (state) => state.userCustomPricing.isLoading;
export const selectIsSearching = (state) => state.userCustomPricing.isSearching;
export const selectIsCreating = (state) => state.userCustomPricing.isCreating;
export const selectIsUpdating = (state) => state.userCustomPricing.isUpdating;
export const selectIsDeleting = (state) => state.userCustomPricing.isDeleting;
export const selectError = (state) => state.userCustomPricing.error;
export const selectSearchError = (state) => state.userCustomPricing.searchError;

export default userCustomPricingSlice.reducer;